// The content of a help article, as the API stores it (vortex-api
// lib/helpContent.ts validates the same shape). Text is plain; **bold** marks
// the exact words on a Scout button or label and nothing else.

export type HelpBlock =
  | { type: 'paragraph'; text: string }
  | { type: 'subheading'; text: string }
  | { type: 'note'; text: string }
  | { type: 'steps'; items: { text: string; mediaId?: string }[] }
  | { type: 'image'; mediaId: string; caption?: string }
  | { type: 'clip'; mediaId: string; caption?: string }
  /** stepsIn: the article whose steps write the video out, when it is not this one. */
  | { type: 'video'; mediaId: string; stepsIn?: string }
  | { type: 'table'; head: string[]; rows: string[][] }
  | { type: 'link'; topicId: string; text?: string };

export type SafetyKind = 'anchor' | 'mob' | 'routes';
export type ArticleType = 'task' | 'troubleshooting' | 'faq' | 'known_issue' | 'video';

export interface NeedsSoftware {
  /** An OTA registry app key (vortex-api lib/otaLive.ts APPS). */
  app: string;
  version: string;
}

/** The OTA registry's apps by key, with the name a reader sees (vortex-api lib/otaLive.ts APPS). */
export const APP_NAMES: Record<string, string> = {
  launcher: 'PolarisOS (launcher)',
  vn: 'VortexNav',
  va: 'VortexAnchor',
  vt: 'VortexTide',
  th: 'TrueHelm',
  vc: 'Vortex Compass',
  gps: 'GPS Diag',
  wv: 'System Services',
};

/** The same apps' Android packages, as a Scout's update check names them. */
export const APP_PACKAGES: Record<string, string> = {
  launcher: 'com.vortexmarine.launcher',
  vn: 'com.vortexmarine.vortexnav',
  va: 'com.vortexmarine.vortexanchor',
  vt: 'com.vortexmarine.vortextide',
  th: 'com.vortexmarine.truehelm',
  vc: 'com.vortexmarine.vortexcompass',
  gps: 'com.vortexmarine.gpsdiag',
  wv: 'com.vortexmarine.webview',
};

/** What one revision of an article holds. */
export interface ArticleContent {
  topicId: string;
  type: ArticleType;
  title: string;
  summary: string;
  whereToFind: string | null;
  /** What the feature needs, shown as Needs, e.g. "A GPS position". */
  requirements: string | null;
  body: HelpBlock[];
  safety: SafetyKind | null;
  keywords: string[];
  needsSoftware: NeedsSoftware[];
  plans: string[];
  related: string[];
  /** Bold labels in no source row, ticked as seen on the Scout. */
  seenLabels: string[];
}

export type MediaKind = 'image' | 'clip' | 'video';

/** What the renderer and the checks need to know about one uploaded file. */
export interface MediaInfo {
  id: string;
  kind: MediaKind;
  alt: string;
  noPersonalData: boolean;
  bytes: number;
  width: number | null;
  height: number | null;
  durationS: number | null;
  /** Where the page loads it from; built by the caller. */
  src: string;
  /** A still frame: a clip shows it when reduced motion is asked for; a video's poster. */
  stillSrc: string | null;
  /** A video's captions (WebVTT). */
  captionsSrc: string | null;
}

/** Another article, as a link to it shows. */
export interface ArticleRef {
  href: string;
  title: string;
}
